import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

export class DateUtils {
  /** Job feeds store Unix seconds; also accept ISO dates without guessing locale formats. */
  static toJobIsoDate(value: string | null | undefined): string | null {
    const input = value?.trim();
    if (!input) return null;

    let timestamp: number;
    if (/^\d+$/.test(input)) {
      const seconds = Number(input);
      // Bound to ISO's four-digit years and reject missing-date sentinels.
      if (!Number.isSafeInteger(seconds) || seconds <= 0 || seconds > 253402300799) {
        return null;
      }
      timestamp = seconds * 1000;
    } else {
      const match = /^(\d{4}-\d{2}-\d{2})(?:T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d{1,3})?(Z|[+-]\d{2}:\d{2})?)?$/.exec(input);
      if (!match) return null;
      const calendarDate = new Date(`${match[1]}T00:00:00Z`);
      if (!Number.isFinite(calendarDate.getTime()) || calendarDate.toISOString().slice(0, 10) !== match[1]) {
        return null;
      }
      // Feed timestamps without a zone are UTC, as in the ingestion service.
      timestamp = Date.parse(input.length === 10 || match[2] ? input : `${input}Z`);
    }

    return Number.isFinite(timestamp) && timestamp > 0
      ? new Date(timestamp).toISOString()
      : null;
  }

  static getTimeFromNow(timestampString: string): string {
    const isoDate = DateUtils.toJobIsoDate(timestampString);
    if (!isoDate) return "";
    const now = dayjs();
    const past = dayjs(isoDate);

    return now.to(past);
  }

  static formatRelativeTime(date: Date | string): string {
    if (!date) return "";
    return dayjs(date).fromNow();
  }

  static formatNumericRelativeTime(
    date: Date | string,
    now: Date = new Date(),
  ): string {
    const value = dayjs(date);
    const reference = dayjs(now);
    if (!value.isValid() || !reference.isValid()) return "";

    const differenceInSeconds = value.diff(reference, "second", true);
    const absoluteSeconds = Math.abs(differenceInSeconds);
    if (absoluteSeconds < 60) return "just now";

    const [unit, secondsPerUnit]: [Intl.RelativeTimeFormatUnit, number] =
      absoluteSeconds < 3_600
        ? ["minute", 60]
        : absoluteSeconds < 86_400
          ? ["hour", 3_600]
          : absoluteSeconds < 2_592_000
            ? ["day", 86_400]
            : absoluteSeconds < 31_536_000
              ? ["month", 2_592_000]
              : ["year", 31_536_000];
    const magnitude = Math.max(1, Math.floor(absoluteSeconds / secondsPerUnit));
    const signedMagnitude = differenceInSeconds < 0 ? -magnitude : magnitude;

    return new Intl.RelativeTimeFormat(undefined, {
      numeric: "always",
    }).format(signedMagnitude, unit);
  }

  static getCurrentYear(): number {
    return dayjs().year();
  }

  static getCurrentMonthName(): string {
    return dayjs().format("MMMM");
  }
}
